// src/pages/Users/Inscription/components/GroupChoiceCard.jsx

import React from "react";

import {
  FaBirthdayCake,
  FaCheck,
  FaClock,
  FaEuroSign,
  FaInfoCircle,
  FaLayerGroup,
  FaLock,
  FaMapMarkerAlt,
  FaStar,
  FaUsers,
} from "react-icons/fa";

import {
  formatEuro,
  getColorMeta,
  getGroupQuota,
  getGroupSchedules,
  normalizeGroupYears,
} from "../utils/inscriptionUtils";

export default function GroupChoiceCard({
  group,
  dojo,

  disabled,
  unavailableReason,

  isQuotaActive,
  recommended,

  onSelect,
  onOpenDetails,
}) {
  const color = getColorMeta(group.color_groupe);
  const quota = getGroupQuota(group);

  const years = normalizeGroupYears(group.annees_naissance);
  const schedules = getGroupSchedules(group);

  const total =
    Number(group.tarif_licence || 0) +
    Number(group.tarif_cotisation || 0);

  /* ---------------------------------------------------------------------- */
  /*                       Texte années de naissance                        */
  /* ---------------------------------------------------------------------- */

  const yearsLabel = (() => {
    if (!years.length) {
      return "Années non renseignées";
    }

    if (years.length === 1) {
      return String(years[0]);
    }

    if (years.length <= 4) {
      return years.join(" · ");
    }

    const numericYears = years
      .map(Number)
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);

    if (!numericYears.length) {
      return years.slice(0, 4).join(" · ");
    }

    return `${numericYears[0]} à ${
      numericYears[numericYears.length - 1]
    }`;
  })();

  return (
    <article
      className={`group relative overflow-hidden rounded-[1.8rem] border bg-white shadow-sm transition dark:bg-slate-900 ${
        disabled
          ? "border-slate-200 dark:border-slate-800"
          : `${color.border} hover:-translate-y-0.5 hover:shadow-lg`
      }`}
    >
      {/* Barre couleur groupe */}

      <div className={`absolute inset-y-0 left-0 w-2 ${color.solid}`} />

      <div className="p-5 pl-7">
        {/* ---------------------------------------------------------------- */}
        {/*                          HEADER                                  */}
        {/* ---------------------------------------------------------------- */}

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {recommended && !disabled ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  <FaStar />
                  Adapté à l'année
                </span>
              ) : null}

              {disabled ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  <FaLock />
                  Fermé
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <FaCheck />
                  Inscription ouverte
                </span>
              )}
            </div>

            <h3 className="mt-3 text-xl font-black leading-tight text-slate-950 dark:text-white">
              {group.nom || "Groupe"}
            </h3>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/*                    Discipline + dojo                            */}
        {/* ---------------------------------------------------------------- */}

        <div className="mt-3 flex flex-wrap gap-2">
          {group.discipline_groupe ? (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${color.light} ${color.text}`}
            >
              <FaLayerGroup className="text-[10px]" />
              {group.discipline_groupe}
            </span>
          ) : null}

          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <FaMapMarkerAlt className="text-[10px]" />
            {dojo?.dojo_nom || "Lieu non renseigné"}
          </span>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/*                          Années                                  */}
        {/* ---------------------------------------------------------------- */}

        <div className="mt-4 rounded-2xl bg-blue-50 p-3.5 dark:bg-blue-500/10">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm dark:bg-blue-500/10 dark:text-blue-300">
              <FaBirthdayCake />
            </span>

            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-blue-500 dark:text-blue-300">
                Années de naissance
              </p>

              <p className="mt-1 font-bold text-blue-900 dark:text-blue-100">
                {yearsLabel}
              </p>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/*                         Horaires                                 */}
        {/* ---------------------------------------------------------------- */}

        {schedules.length > 0 ? (
          <div className="mt-4">
            <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
              <FaClock />
              Horaires
            </p>

            <div className="flex flex-wrap gap-2">
              {schedules.slice(0, 3).map((schedule, index) => (
                <span
                  key={`${schedule}-${index}`}
                  className="rounded-xl bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  {schedule}
                </span>
              ))}

              {schedules.length > 3 ? (
                <span className="rounded-xl bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  +{schedules.length - 3}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* ---------------------------------------------------------------- */}
        {/*                       Quota + tarif                              */}
        {/* ---------------------------------------------------------------- */}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/60">
            <div className="flex items-center gap-2 text-slate-400">
              <FaUsers className="text-xs" />

              <p className="text-[10px] font-black uppercase tracking-wide">
                Places
              </p>
            </div>

            <p className="mt-1.5 text-sm font-black text-slate-900 dark:text-white">
              {isQuotaActive && quota.hasQuota
                ? `${quota.members} / ${quota.quota}`
                : "Non limité"}
            </p>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3 dark:border-emerald-500/10 dark:bg-emerald-500/10">
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300">
              <FaEuroSign className="text-xs" />

              <p className="text-[10px] font-black uppercase tracking-wide">
                Tarif
              </p>
            </div>

            <p className="mt-1.5 text-sm font-black text-emerald-700 dark:text-emerald-300">
              {formatEuro(total)}
            </p>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/*                      Progression quota                           */}
        {/* ---------------------------------------------------------------- */}

        {isQuotaActive && quota.hasQuota ? (
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold text-slate-500 dark:text-slate-400">
              <span>Remplissage</span>

              <span>{quota.percent}%</span>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div
                className={`h-full rounded-full transition-all ${
                  quota.isFull ? "bg-rose-500" : color.solid
                }`}
                style={{
                  width: `${Math.min(quota.percent, 100)}%`,
                }}
              />
            </div>
          </div>
        ) : null}

        {/* ---------------------------------------------------------------- */}
        {/*                   Groupe indisponible                            */}
        {/* ---------------------------------------------------------------- */}

        {disabled ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-3 dark:border-amber-500/20 dark:bg-amber-500/10">
            <div className="flex gap-2">
              <FaLock className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-300" />

              <p className="text-xs font-bold leading-5 text-amber-700 dark:text-amber-300">
                {unavailableReason || "Inscription impossible"}
              </p>
            </div>
          </div>
        ) : null}

        {/* ---------------------------------------------------------------- */}
        {/*                          Actions                                 */}
        {/* ---------------------------------------------------------------- */}

        <div className="mt-5 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={onOpenDetails}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:border-indigo-500/30 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
          >
            <FaInfoCircle />
            Voir les détails
          </button>

          <button
            type="button"
            onClick={onSelect}
            disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-black transition ${
              disabled
                ? "cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
                : "bg-[#013E8E] text-white shadow-sm hover:bg-[#012f6d] hover:shadow-md dark:bg-indigo-500 dark:hover:bg-indigo-400"
            }`}
          >
            {disabled ? (
              <>
                <FaLock />
                Indisponible
              </>
            ) : (
              <>
                <FaCheck />
                Choisir ce groupe
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}